import type { ReachAxes } from './boat/reach.ts';
import { SKIFF_PLANING_KMH } from './boat/limits.ts';
import { DEFAULT_HULL, type HullDimensions } from './hullSize.ts';
import { BOW_OFFSET_PX, CRASH_HARSHNESS, TURN_TAX } from './constants.ts';
import { DEFAULT_WIND_FEEL, type WindFeel } from './environment/wind.ts';
import type { RaceReplay } from './highScores/types.ts';
import type { MapDocument } from './map/types.ts';
import type { MatchMovement } from './match/types.ts';
import { groupReplayStepsByRound, matchReplayOrder } from './match/replayOrder.ts';
import { separateFleet } from './modules/host.ts';
import { clamp } from './math.ts';
import { createBoatState, resolveMove } from './movement.ts';
import { projectMovePath, type MovePath } from './path.ts';
import { completeResolve, tryCommit, tryCommitFleet } from './turn.ts';
import { planWithFeel, seatCommitArgs, type RingMode } from './turnFeel.ts';
import type { BoatState } from './types.ts';

export { nextRingMode, RING_MODES, type RingMode } from './turnFeel.ts';

export type ReconstructedReplay = {
  boats: BoatState[];
  paths: MovePath[];
};

/** Session feel for a replay. The ring mode matches the live race. Omitted is off. */
export type ReplayBoatFeel = {
  reach?: ReachAxes | null;
  bowOffsetPx?: number;
  startSpeed?: number;
  /** Pace at the start line. Omitted follows speed: rest is the small disk, moving is full. */
  startDrive?: 0 | 1 | 2;
  planingSpeedKmh?: number;
  ringMode?: RingMode;
};

export const REPLAY_PLAYBACK_RATES = [0.25, 0.5, 1, 1.25, 1.5] as const;
export type ReplayPlaybackRate = (typeof REPLAY_PLAYBACK_RATES)[number];
export const DEFAULT_REPLAY_PLAYBACK_RATE: ReplayPlaybackRate = 0.5;

export function isReplayPlaybackRate(value: number): value is ReplayPlaybackRate {
  return (REPLAY_PLAYBACK_RATES as readonly number[]).includes(value);
}

export function formatReplayPlaybackRate(rate: ReplayPlaybackRate): string {
  return Number.isInteger(rate) ? `${rate.toFixed(1)}x` : `${rate}x`;
}

/**
 * `turnByTurn` is the original hot-seat replay: one seat animates per step, in the exact
 * alternating order the live match produced, with the camera handoff glide between seats.
 * `simultaneous` groups those steps into rounds (every seat's Nth move shares round N — see
 * `groupReplayStepsByRound`) and animates every still-racing seat's hull at once over a static,
 * zoomed-to-fit camera. Only meaningful with more than one seat; a solo replay behaves the same
 * either way (one seat per round).
 */
export const REPLAY_PLAYBACK_MODES = ['turnByTurn', 'simultaneous'] as const;
export type ReplayPlaybackMode = (typeof REPLAY_PLAYBACK_MODES)[number];
export const DEFAULT_REPLAY_PLAYBACK_MODE: ReplayPlaybackMode = 'simultaneous';

export function isReplayPlaybackMode(value: string): value is ReplayPlaybackMode {
  return (REPLAY_PLAYBACK_MODES as readonly string[]).includes(value);
}

export function formatReplayPlaybackMode(mode: ReplayPlaybackMode): string {
  return mode === 'simultaneous' ? 'All at once' : 'Turn by turn';
}

/**
 * Replays stored clicks through the same commit rules as play. `null` when a destination is no
 * longer legal on this map (the course changed, or the file is corrupt).
 */
export function reconstructReplay(
  map: MapDocument,
  replay: RaceReplay,
  crashHarshness: number = CRASH_HARSHNESS,
  turnTax: number = TURN_TAX,
  windFeel: WindFeel = DEFAULT_WIND_FEEL,
  feel: ReplayBoatFeel = {},
): ReconstructedReplay | null {
  let boat = createBoatState({
    position: replay.start.position,
    heading: replay.start.heading,
    speed: feel.startSpeed ?? 0,
    drive: feel.startDrive,
  });
  const boats: BoatState[] = [boat];
  const paths: MovePath[] = [];
  const reach = feel.reach ?? null;
  const bowOffsetPx = feel.bowOffsetPx ?? BOW_OFFSET_PX;
  const planingSpeedKmh = feel.planingSpeedKmh ?? SKIFF_PLANING_KMH;
  const ringMode = feel.ringMode ?? 'off';

  for (const move of replay.moves) {
    const committed = planWithFeel(boat, move.destination, map, {
      radius: move.radius,
      reach,
      ringMode,
      planingSpeedKmh,
      bowOffsetPx,
      crashHarshness,
      turnTax,
      windFeel,
    });

    if (!committed) {
      return null;
    }

    const path = committed.path ?? projectMovePath(boat, move.destination, 'arc', turnTax);

    if (!path) {
      return null;
    }

    boat = completeResolve(committed.boat).boat;
    boats.push(boat);
    paths.push(path);
  }

  return { boats, paths };
}

type ReplayRingFeel = {
  reach: ReachAxes | null;
  planingSpeedKmh: number;
  ringMode: RingMode;
};

/**
 * Reach, coast, and kept speed for one recorded click, so the live commit and a
 * reconstruction are shaped by the same ring. Shared by the solo and the
 * fleet-wide rebuild; the two must not drift apart.
 */
function replayMoveShape(
  boat: BoatState,
  move: { destination: { x: number; y: number }; radius: number },
  feel: ReplayRingFeel,
  turnTax: number,
): {
  reach: ReachAxes | null;
  radius: number;
  coastPx: number;
  keptSpeed: number | undefined;
} {
  const args = seatCommitArgs(boat, move.destination, {
    radius: move.radius,
    reach: feel.reach,
    ringMode: feel.ringMode,
    planingSpeedKmh: feel.planingSpeedKmh,
    bowOffsetPx: BOW_OFFSET_PX,
    crashHarshness: CRASH_HARSHNESS,
    turnTax,
    windFeel: DEFAULT_WIND_FEEL,
  });

  return {
    reach: args.reach,
    radius: args.radius,
    coastPx: args.coastPx,
    keptSpeed: args.keptSpeed,
  };
}

export type MatchReplaySeat = {
  replay: RaceReplay;
  feel?: ReplayBoatFeel;
  dimensions?: HullDimensions;
};

/**
 * Rebuild a whole hot-seat match, rather than one seat at a time.
 *
 * A seat's path stops being reconstructible on its own the moment boats can
 * push each other: where a hull starts its turn depends on what happened during
 * somebody else's. So this carries a live pose for every seat and hands the
 * fleet to the same resolver the race used.
 *
 * `sequential` walks `matchReplayOrder` one mover at a time, everyone else
 * parked. `simultaneous` buckets those steps back into rounds and sweeps every
 * path in the round together, which is what `resolveRound` did live. One mover
 * against parked hulls would miss a contact both boats steered into.
 *
 * Whether hulls hit each other is the host's session flag, the same one the
 * live race set. A missing boat-collision module leaves that flag inactive,
 * so this rebuilds terrain only and does not invent shoves.
 *
 * `boats[i]` is the pose that seat actually started its i-th move from, which is
 * not always where its previous move ended.
 */
export function reconstructMatchReplay(
  map: MapDocument,
  seats: readonly MatchReplaySeat[],
  crashHarshness: number = CRASH_HARSHNESS,
  turnTax: number = TURN_TAX,
  windFeel: WindFeel = DEFAULT_WIND_FEEL,
  movement: MatchMovement = 'sequential',
): (ReconstructedReplay | null)[] {
  const live = seats.map((seat) =>
    createBoatState({
      position: seat.replay.start.position,
      heading: seat.replay.start.heading,
      speed: seat.feel?.startSpeed ?? 0,
      drive: seat.feel?.startDrive,
    }),
  );
  const out: (ReconstructedReplay | null)[] = live.map((boat) => ({
    boats: [boat],
    paths: [],
  }));

  if (movement === 'simultaneous') {
    reconstructSimultaneousRounds(map, seats, live, out, crashHarshness, turnTax, windFeel);
    return out;
  }

  for (const step of matchReplayOrder(seats.map((seat) => seat.replay.moves.length))) {
    const seat = seats[step.seatId];
    const rebuilt = out[step.seatId];
    const move = seat?.replay.moves[step.localIndex];
    const boat = live[step.seatId];

    if (!seat || !rebuilt || !move || !boat) {
      continue;
    }

    // Where this hull actually is now, shoves included.
    rebuilt.boats[step.localIndex] = boat;

    const shape = replayMoveShape(
      boat,
      move,
      {
        reach: seat.feel?.reach ?? null,
        planingSpeedKmh: seat.feel?.planingSpeedKmh ?? SKIFF_PLANING_KMH,
        ringMode: seat.feel?.ringMode ?? 'off',
      },
      turnTax,
    );
    const committed = tryCommitFleet(
      'planning',
      boat,
      move.destination,
      shape.radius,
      map,
      crashHarshness,
      turnTax,
      windFeel,
      shape.reach,
      seat.feel?.bowOffsetPx ?? BOW_OFFSET_PX,
      shape.coastPx,
      shape.keptSpeed,
      {
        id: step.seatId,
        hull: seat.dimensions ?? DEFAULT_HULL,
        others: seats.flatMap((other, id) =>
          id === step.seatId || !live[id]
            ? []
            : [
                {
                  id,
                  boat: live[id]!,
                  dimensions: other.dimensions ?? DEFAULT_HULL,
                },
              ],
        ),
      },
    );

    if (!committed) {
      out[step.seatId] = null;
      continue;
    }

    const path =
      committed.commit.path ?? projectMovePath(boat, move.destination, 'arc', turnTax);

    if (!path) {
      out[step.seatId] = null;
      continue;
    }

    live[step.seatId] = completeResolve(committed.commit.boat).boat;
    rebuilt.boats[step.localIndex + 1] = live[step.seatId]!;
    rebuilt.paths[step.localIndex] = path;

    for (const run of committed.runs) {
      if (run.id === step.seatId || !run.shoved || !live[run.id]) {
        continue;
      }

      live[run.id] = run.boat;
    }
  }

  return out;
}

/**
 * One round at a time, every locked path on the shared clock. A seat with no click
 * this round is a path that stays put, and a shove of that hull still lands in `live`
 * so its next recorded move starts from where it was pushed.
 */
function reconstructSimultaneousRounds(
  map: MapDocument,
  seats: readonly MatchReplaySeat[],
  live: BoatState[],
  out: (ReconstructedReplay | null)[],
  crashHarshness: number,
  turnTax: number,
  windFeel: WindFeel,
): void {
  const rounds = groupReplayStepsByRound(
    matchReplayOrder(seats.map((seat) => seat.replay.moves.length)),
  );

  for (const round of rounds) {
    const aimed = new Map<number, { localIndex: number; path: MovePath | null }>();
    const fleet = seats.map((seat, id) => {
      const boat = live[id]!;
      const parked = {
        id,
        boat,
        dimensions: seat.dimensions ?? DEFAULT_HULL,
        intended: null,
      };
      const step = round.find((entry) => entry.seatId === id);
      const rebuilt = out[id];
      const move = step ? seat.replay.moves[step.localIndex] : undefined;

      if (!step || !rebuilt || !move) {
        return parked;
      }

      rebuilt.boats[step.localIndex] = boat;
      const shape = replayMoveShape(
        boat,
        move,
        {
          reach: seat.feel?.reach ?? null,
          planingSpeedKmh: seat.feel?.planingSpeedKmh ?? SKIFF_PLANING_KMH,
          ringMode: seat.feel?.ringMode ?? 'off',
        },
        turnTax,
      );
      const accepted = tryCommit(
        'planning',
        boat,
        move.destination,
        shape.radius,
        map,
        crashHarshness,
        turnTax,
        windFeel,
        shape.reach,
        seat.feel?.bowOffsetPx ?? BOW_OFFSET_PX,
        shape.coastPx,
        shape.keptSpeed,
      );

      if (!accepted) {
        out[id] = null;
        return parked;
      }

      const path = projectMovePath(boat, move.destination, 'arc', turnTax) ?? accepted.path;
      aimed.set(id, { localIndex: step.localIndex, path });

      return {
        id,
        boat,
        dimensions: seat.dimensions ?? DEFAULT_HULL,
        intended: path,
        landedIfClear: resolveMove(boat, move.destination, turnTax, shape.keptSpeed),
      };
    });
    const swept = separateFleet(map, fleet, crashHarshness, turnTax);

    for (const run of swept.runs) {
      live[run.id] = run.boat;
      const intent = aimed.get(run.id);
      const rebuilt = out[run.id];

      if (!intent || !rebuilt) {
        continue;
      }

      const path = run.path ?? intent.path;

      if (!path) {
        out[run.id] = null;
        continue;
      }

      rebuilt.boats[intent.localIndex + 1] = run.boat;
      rebuilt.paths[intent.localIndex] = path;
    }
  }
}

/**
 * Speed shown during a replay turn. Linear in time so the camera and HUD do not snap
 * to the committed landing speed the moment the hull leaves.
 */
export function replaySpeed(fromSpeed: number, toSpeed: number, timeT: number): number {
  const t = clamp(timeT, 0, 1);
  return fromSpeed + (toSpeed - fromSpeed) * t;
}

/**
 * HUD / camera speed across a replay step. Solo already lerps from this boat's previous
 * committed speed; a multi-seat turn-by-turn (or All-at-once representative change)
 * must use the previous *step*'s landing as the origin so a seat change does not snap
 * the dial and auto-zoom. Hull travel still uses the stepping boat's own from/to.
 */
export function replayDisplaySpeed(
  previousLandingSpeed: number | undefined,
  fromSpeed: number,
  toSpeed: number,
  timeT: number,
): number {
  return replaySpeed(previousLandingSpeed ?? fromSpeed, toSpeed, timeT);
}

export type ReplaySpeedSeat = {
  seatId: number;
  boats: readonly { speed: number }[];
};

export type ReplaySpeedStep = {
  seatId: number;
  localIndex: number;
};

/** Landing speed of the flat step before `stepIndex` (turn-by-turn). */
export function previousReplayLandingSpeed(
  seats: readonly ReplaySpeedSeat[],
  steps: readonly ReplaySpeedStep[],
  stepIndex: number,
): number | undefined {
  if (stepIndex <= 0) {
    return undefined;
  }

  const previous = steps[stepIndex - 1];

  if (!previous) {
    return undefined;
  }

  const boats = seats.find((entry) => entry.seatId === previous.seatId)?.boats;
  return boats?.[previous.localIndex + 1]?.speed;
}

/** Landing speed of the previous round's representative (All at once). */
export function previousRoundLandingSpeed(
  seats: readonly ReplaySpeedSeat[],
  rounds: readonly ReplaySpeedStep[][],
  roundIndex: number,
): number | undefined {
  if (roundIndex <= 0) {
    return undefined;
  }

  const previousId = rounds[roundIndex - 1]?.[0]?.seatId;

  if (previousId === undefined) {
    return undefined;
  }

  return seats.find((entry) => entry.seatId === previousId)?.boats[roundIndex]?.speed;
}

/**
 * Arc-length parameter for a turn whose speed lerps from `fromSpeed` to `toSpeed`.
 * Time is still even; distance matches the integral of that speed so a standing start
 * accelerates and a slower next turn eases off instead of slamming.
 */
export function travelProgressFromSpeeds(
  fromSpeed: number,
  toSpeed: number,
  timeT: number,
): number {
  const t = clamp(timeT, 0, 1);
  const start = Math.max(0, fromSpeed);
  const end = Math.max(0, toSpeed);
  const denom = start + end;

  if (denom < 1e-9) {
    return t;
  }

  return (2 * start * t + (end - start) * t * t) / denom;
}

/** d(pathT)/d(timeT) for `travelProgressFromSpeeds`. */
export function travelProgressSlope(
  fromSpeed: number,
  toSpeed: number,
  timeT: number,
): number {
  const t = clamp(timeT, 0, 1);
  const start = Math.max(0, fromSpeed);
  const end = Math.max(0, toSpeed);
  const denom = start + end;

  if (denom < 1e-9) {
    return 1;
  }

  return (2 * start + 2 * (end - start) * t) / denom;
}

/**
 * Cubic pathT with given endpoint slopes. Slopes stay non-negative and
 * `m0 + m1 ≤ 3` so the hull never reverses. Incoming `m0` is kept; `m1` yields.
 */
export function travelProgressHermite(
  startSlope: number,
  endSlope: number,
  timeT: number,
): number {
  const t = clamp(timeT, 0, 1);
  const { start, end } = clampJoinSlopes(startSlope, endSlope);
  const t2 = t * t;
  const t3 = t2 * t;
  return 3 * t2 - 2 * t3 + start * (t3 - 2 * t2 + t) + end * (t3 - t2);
}

function clampJoinSlopes(
  startSlope: number,
  endSlope: number,
): { start: number; end: number } {
  let start = Math.max(0, startSlope);
  let end = Math.max(0, endSlope);

  if (start > 3) {
    start = 3;
  }

  if (end > 3 - start) {
    end = 3 - start;
  }

  return { start, end };
}

/** pathT slopes actually used for a joined turn (after the monotonic clamp). */
export function travelJoinSlopes(
  fromSpeed: number,
  toSpeed: number,
  pathLength: number,
  incomingWorldSpeed?: number,
): { start: number; end: number } {
  const length = Math.max(0, pathLength);
  const start =
    incomingWorldSpeed !== undefined && length > 1e-9
      ? incomingWorldSpeed / length
      : travelProgressSlope(fromSpeed, toSpeed, 0);
  const end = travelProgressSlope(fromSpeed, toSpeed, 1);
  return clampJoinSlopes(start, end);
}

/** World-space hull speed along a joined all-at-once turn. */
export function travelJoinWorldSpeed(
  fromSpeed: number,
  toSpeed: number,
  timeT: number,
  pathLength: number,
  incomingWorldSpeed?: number,
): number {
  const t = clamp(timeT, 0, 1);
  const { start, end } = travelJoinSlopes(
    fromSpeed,
    toSpeed,
    pathLength,
    incomingWorldSpeed,
  );
  const t2 = t * t;
  const slope =
    6 * t -
    6 * t2 +
    start * (3 * t2 - 4 * t + 1) +
    end * (3 * t2 - 2 * t);
  return slope * Math.max(0, pathLength);
}

/** Actual exit world speed of the joined profile (next turn's incoming). */
export function travelJoinExitWorldSpeed(
  fromSpeed: number,
  toSpeed: number,
  pathLength: number,
  incomingWorldSpeed?: number,
): number {
  return travelJoinWorldSpeed(fromSpeed, toSpeed, 1, pathLength, incomingWorldSpeed);
}

/**
 * Incoming world speed for round `roundIndex` after walking every earlier
 * join, so a clamp on turn N does not reappear as a hitch on turn N+1.
 */
export function replayIncomingWorldSpeed(
  boats: readonly { speed: number }[],
  pathLengths: readonly number[],
  roundIndex: number,
): number | undefined {
  if (roundIndex <= 0) {
    return undefined;
  }

  let incoming: number | undefined;

  for (let index = 0; index < roundIndex; index += 1) {
    incoming = travelJoinExitWorldSpeed(
      boats[index]?.speed ?? 0,
      boats[index + 1]?.speed ?? 0,
      pathLengths[index] ?? 0,
      incoming,
    );
  }

  return incoming;
}

/**
 * All-at-once path parameter: finish this path in one turn, starting at the
 * previous turn's world exit so a speed change does not hitch at the join.
 */
export function travelProgressFromJoin(
  fromSpeed: number,
  toSpeed: number,
  timeT: number,
  pathLength: number,
  incomingWorldSpeed?: number,
): number {
  const { start, end } = travelJoinSlopes(
    fromSpeed,
    toSpeed,
    pathLength,
    incomingWorldSpeed,
  );
  return travelProgressHermite(start, end, timeT);
}
